import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import LendingOperationsApi from "../../../../../../infrastructure/api/lendingOperations";
import CreditInquiryApi from "../../../../../../infrastructure/api/creditInquiry";
import MehrsanOperationsApi from "../../../../../../infrastructure/api/mehrsanOperations";
import ProjectWorkflowFoundationApi from "../../../../../../infrastructure/api/projectWorkflowFoundation";
import {
  getEntityId,
  parseApiErrorMessage,
} from "../../../../../components/request/case-tabs/mehrsanOperationsUtils";
import {
  LOAN_PROCESS_TYPE_APPLICATION,
} from "../../../../../components/request/case-tabs/lendingOperationsConstants";
import {
  MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
  resolveOperationsSource,
} from "../../../../../components/request/case-tabs/mehrsanOperationsConstants";
import {
  GATE_PILL_LOAN,
  GATE_PILL_MEHRSAN,
  GATE_WARNING_TEXT,
  GATE_WARNING_TITLE,
  MEHRSAN_STAGE_STEPS,
  STEP_STATUS,
} from "./loanMehrsanStageConstants";
import IriaPortalDetailPanel, {
  pickIriaPortalFields,
} from "../../lending/IriaPortalDetailPanel";
import IriaPortalStagesTimeline from "../../lending/IriaPortalStagesTimeline";
import {
  buildLoanStageSteps,
  buildMehrsanStageSteps,
  resolveLoanWorkflowTimeline,
  buildStepSavePayload,
  countCompletedSteps,
  getLoanStageTotalCount,
  mapUiStatusToLoanApiStatus,
  mapUiStatusToMehrsanApiStatus,
  resolveProcessOverallStatus,
} from "./loanMehrsanStageAdapter";
import LoanMehrsanProcessStepsTable from "./LoanMehrsanProcessStepsTable";
import LoanMehrsanStepEditDialog from "./LoanMehrsanStepEditDialog";
import LendtechSelectionCard from "./LendtechSelectionCard";
import InitialSiteVisitAssignmentCard from "./InitialSiteVisitAssignmentCard";
import MehrsunSendRegistrationCard from "./MehrsunSendRegistrationCard";
import { resolveActiveLoanProcessId } from "./WorkflowActionsCard";

const IRIA_PARTNER_CODE = "IRIA_PAY";

const normalizeList = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

const findPowerPurchaseProcess = (processes) =>
  processes.find(
    (p) =>
      Number(p.processType ?? p.ProcessType) === MEHRSAN_PROCESS_TYPE_POWER_PURCHASE
  ) || null;

const pickLoanField = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const resolveActiveLoanProcess = (processes) => {
  if (!Array.isArray(processes)) return null;
  const activeId = resolveActiveLoanProcessId(processes);
  if (!activeId) return null;
  return (
    processes.find((process) => Number(getEntityId(process)) === Number(activeId)) ||
    null
  );
};

const formatLendingPartnerLabel = (process) => {
  if (!process) return "";
  const title = pickLoanField(process, "lendingPartnerTitle", "LendingPartnerTitle");
  const code = pickLoanField(process, "lendingPartnerCode", "LendingPartnerCode");
  if (title && code) return `${title} (${code})`;
  return title || code || "";
};

const LoanMehrsanStagePanel = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  workflowSource,
  loanMehrsanGate,
  loanMehrsanGateLoading = false,
  canProceedToOperational = false,
  canEdit = false,
  loanOperationsReadOnly = true,
  onReloadGate,
  onNavigateToOperational,
  onRequestTerminated,
  initialDistributionCompanyId = null,
}) => {
  const operationsSource = workflowSource || resolveOperationsSource(detailApi);
  const [loading, setLoading] = useState(false);
  const [mehrsanProcesses, setMehrsanProcesses] = useState([]);
  const [loanProcesses, setLoanProcesses] = useState([]);
  const [partners, setPartners] = useState([]);
  const [editState, setEditState] = useState({ open: false, domain: null, step: null });
  const [saving, setSaving] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateNote, setTerminateNote] = useState("");
  const [terminating, setTerminating] = useState(false);
  const [lendtechSendBusy, setLendtechSendBusy] = useState(false);
  const [lendtechChangeBusy, setLendtechChangeBusy] = useState(false);
  const [iriaNextBusy, setIriaNextBusy] = useState(false);
  const [mehrsunStartToken, setMehrsunStartToken] = useState(0);
  const [highlightMehrsunSend, setHighlightMehrsunSend] = useState(false);
  const [autoCreditInquirySubmitted, setAutoCreditInquirySubmitted] = useState(false);

  const creditInquiryApiPath =
    operationsSource === "Management"
      ? "SLN/Management/CheckNewRequest"
      : "SLN/SolarentOperator/CheckNewRequest";

  const openMehrsunRegistrationFlow = useCallback(() => {
    setHighlightMehrsunSend(true);
    setMehrsunStartToken((t) => t + 1);
    toast.info(
      "شرکت توزیع را انتخاب کنید؛ سپس «ارسال به مهرسان» (اتوماتیک) یا «ثبت دستی» را بزنید."
    );
  }, []);

  const loadData = useCallback(async () => {
    if (!requestInfoId) return;
    setLoading(true);
    try {
      const [mehrsanRes, loanRes, partnersRes, creditSummary] = await Promise.all([
        MehrsanOperationsApi.getByRequestId(operationsSource, requestInfoId),
        LendingOperationsApi.getByRequestId(operationsSource, requestInfoId),
        LendingOperationsApi.getPartners(operationsSource),
        CreditInquiryApi.getSummary(creditInquiryApiPath, requestInfoId).catch(
          () => null
        ),
      ]);

      const mehrsanList =
        mehrsanRes?.isSuccess === false ? [] : normalizeList(mehrsanRes);
      const loanList = loanRes?.isSuccess === false ? [] : normalizeList(loanRes);

      const mehrsanWithLogs = await Promise.all(
        mehrsanList.map(async (item) => {
          const id = getEntityId(item);
          if (!id) return item;
          try {
            const detail = await MehrsanOperationsApi.getById(operationsSource, id);
            return detail?.data ?? detail?.Data ?? item;
          } catch {
            return item;
          }
        })
      );

      const loanWithLogs = await Promise.all(
        loanList.map(async (item) => {
          const id = getEntityId(item);
          if (!id) return item;
          try {
            const detail = await LendingOperationsApi.getById(operationsSource, id);
            return detail?.data ?? detail?.Data ?? item;
          } catch {
            return item;
          }
        })
      );

      setMehrsanProcesses(mehrsanWithLogs);
      setLoanProcesses(loanWithLogs);
      setPartners(partnersRes?.isSuccess === false ? [] : normalizeList(partnersRes));
      setAutoCreditInquirySubmitted(
        Boolean(creditSummary?.hasAnyAttempt ?? creditSummary?.HasAnyAttempt)
      );
    } catch {
      setMehrsanProcesses([]);
      setLoanProcesses([]);
      setPartners([]);
      setAutoCreditInquirySubmitted(false);
    } finally {
      setLoading(false);
    }
  }, [creditInquiryApiPath, operationsSource, requestInfoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mehrsanSteps = useMemo(
    () => buildMehrsanStageSteps(mehrsanProcesses),
    [mehrsanProcesses]
  );
  const loanSteps = useMemo(
    () => buildLoanStageSteps(loanProcesses),
    [loanProcesses]
  );
  const activeLoanProcess = useMemo(
    () => resolveActiveLoanProcess(loanProcesses),
    [loanProcesses]
  );
  const activeLendingPartnerLabel = useMemo(
    () => formatLendingPartnerLabel(activeLoanProcess),
    [activeLoanProcess]
  );
  const activeLoanStatusTitle = useMemo(() => {
    if (!activeLoanProcess) return "";
    return (
      pickLoanField(activeLoanProcess, "currentStatusTitle", "CurrentStatusTitle") ||
      ""
    );
  }, [activeLoanProcess]);
  const activeLoanTrackingCode = useMemo(() => {
    if (!activeLoanProcess) return "";
    return (
      pickLoanField(activeLoanProcess, "trackingCode", "TrackingCode") ||
      pickLoanField(activeLoanProcess, "referenceNumber", "ReferenceNumber") ||
      ""
    );
  }, [activeLoanProcess]);
  const iriaExternalApplicationId = useMemo(() => {
    if (!activeLoanProcess) return "";
    return String(
      pickLoanField(activeLoanProcess, "trackingCode", "TrackingCode") || ""
    ).trim();
  }, [activeLoanProcess]);
  const activeLendingPartnerCode = useMemo(
    () =>
      pickLoanField(activeLoanProcess, "lendingPartnerCode", "LendingPartnerCode") || "",
    [activeLoanProcess]
  );
  const activeLendingPartnerId = useMemo(
    () =>
      pickLoanField(activeLoanProcess, "lendingPartnerId", "LendingPartnerId") || null,
    [activeLoanProcess]
  );
  const isIriaLoan = activeLendingPartnerCode === IRIA_PARTNER_CODE;
  const canIriaNext =
    isIriaLoan && /^\d+$/.test(iriaExternalApplicationId) && !!getEntityId(activeLoanProcess);
  const lendtechQueueSearchHint =
    iriaExternalApplicationId || String(requestInfoId || "");

  const mehrsanCompleted = countCompletedSteps(mehrsanSteps);
  const loanCompleted = countCompletedSteps(loanSteps);
  const loanRequired = loanMehrsanGate?.loanRequired !== false;
  const canStartMehrsanOperations =
    loanMehrsanGate?.canStartMehrsanOperations === true;
  const canTerminateDueToSiteVisitRejection =
    loanMehrsanGate?.canTerminateDueToSiteVisitRejection === true;
  const siteVisitOutcomeBlockReason =
    loanMehrsanGate?.siteVisitOutcomeBlockReason || "";
  const siteVisitSupervisorDescription =
    loanMehrsanGate?.siteVisitSupervisorDescription || "";
  const siteVisitOutcomeStatusTitle =
    loanMehrsanGate?.siteVisitOutcomeStatusTitle || "";
  const isApplicantWithdrawal =
    String(siteVisitOutcomeStatusTitle || "").includes("انصراف");
  const isAwaitingDistributionReview =
    loanMehrsanGate?.isAwaitingDistributionReview === true;
  const canEditMehrsanSteps = canEdit && canStartMehrsanOperations;

  const mehrsanOverall = resolveProcessOverallStatus(
    mehrsanCompleted,
    MEHRSAN_STAGE_STEPS.length
  );
  const loanOverall = resolveProcessOverallStatus(
    loanCompleted,
    getLoanStageTotalCount(loanSteps),
    !loanRequired
  );

  const handleSendToLendtech = async (lendingPartnerId) => {
    if (!lendingPartnerId) {
      toast.error("انتخاب لندتک الزامی است.");
      return;
    }
    if (loanProcesses.length > 0) {
      toast.info("فرآیند وام برای این پرونده قبلاً ثبت شده است.");
      return;
    }

    setLendtechSendBusy(true);
    try {
      const selectedPartner = (partners || []).find(
        (p) => Number(p?.id ?? p?.Id) === Number(lendingPartnerId)
      );
      const selectedCode = String(
        selectedPartner?.code ?? selectedPartner?.Code ?? ""
      ).toUpperCase();

      const res = await LendingOperationsApi.create(operationsSource, {
        RequestInfoId: Number(requestInfoId),
        LendingPartnerId: Number(lendingPartnerId),
        ProcessType: LOAN_PROCESS_TYPE_APPLICATION,
      });
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ثبت فرآیند وام ناموفق بود."));
      }

      const connectorMessage =
        res?.message ||
        res?.Message ||
        res?.data?.connectorMessage ||
        res?.data?.ConnectorMessage ||
        res?.Data?.connectorMessage ||
        res?.Data?.ConnectorMessage;

      if (selectedCode === IRIA_PARTNER_CODE) {
        toast.success(
          connectorMessage ||
            "پرونده در ایریاپی ثبت شد و مرحله اول (ارسال به ایریا) اجرا شد."
        );
      } else {
        toast.success(
          connectorMessage || "پرونده به لندتک انتخاب‌شده ارسال شد."
        );
      }
      await loadData();
      onReloadGate?.();
    } catch (err) {
      toast.error(err?.message || "ارسال به لندتک ناموفق بود.");
      // Create may have succeeded before Iria submit failed — refresh UI state.
      try {
        await loadData();
        onReloadGate?.();
      } catch {
        /* ignore reload errors */
      }
    } finally {
      setLendtechSendBusy(false);
    }
  };

  const handleChangeLendingPartner = async (newPartnerId, reason) => {
    const loanProcessId = getEntityId(activeLoanProcess);
    if (!loanProcessId) {
      toast.warning("فرایند وام فعال یافت نشد.");
      throw new Error("missing loan process");
    }
    setLendtechChangeBusy(true);
    try {
      const res = await LendingOperationsApi.changePartner(
        operationsSource,
        loanProcessId,
        { newPartnerId, reason }
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "تغییر لندتک ناموفق بود."));
      }
      toast.success("لندتک با موفقیت تغییر کرد.");
      await loadData();
      onReloadGate?.();
    } catch (err) {
      toast.error(err?.message || "تغییر لندتک ناموفق بود.");
      throw err;
    } finally {
      setLendtechChangeBusy(false);
    }
  };

  const handleIriaNextStep = async () => {
    const loanProcessId = getEntityId(activeLoanProcess);
    if (!loanProcessId || !canIriaNext) {
      toast.warning("شناسه ایریاپی یافت نشد. ابتدا پرونده باید در ایریا ثبت شده باشد.");
      return;
    }
    setIriaNextBusy(true);
    try {
      const res = await LendingOperationsApi.iriaPortalNextStep(
        operationsSource,
        loanProcessId
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "اجرای مرحله بعد ایریاپی ناموفق بود."));
      }
      const data = res?.data ?? res?.Data ?? {};
      toast.success(
        data?.connectorMessage ||
          data?.ConnectorMessage ||
          res?.message ||
          "مرحله بعد در ایریاپی انجام شد"
      );
      await loadData();
      onReloadGate?.();
    } catch (err) {
      toast.error(err?.message || "اجرای مرحله بعد ایریاپی ناموفق بود.");
    } finally {
      setIriaNextBusy(false);
    }
  };

  const ensureMehrsanProcess = async () => {
    const existing = findPowerPurchaseProcess(mehrsanProcesses);
    if (existing) return getEntityId(existing);

    const res = await MehrsanOperationsApi.create(operationsSource, {
      RequestInfoId: Number(requestInfoId),
      ProcessType: MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
      Title: "قرارداد خرید برق مهرسان",
    });
    if (res?.isSuccess === false) {
      throw new Error(parseApiErrorMessage(res, "ایجاد فرآیند مهرسان ناموفق بود."));
    }
    const id =
      res?.data?.id ?? res?.data?.Id ?? res?.Data?.id ?? res?.Data?.Id ?? null;
    return id;
  };

  const ensureLoanProcess = async (lendingPartnerId) => {
    if (loanProcesses.length > 0) return getEntityId(loanProcesses[0]);
    if (!lendingPartnerId) {
      throw new Error("انتخاب لندتک الزامی است.");
    }

    const res = await LendingOperationsApi.create(operationsSource, {
      RequestInfoId: Number(requestInfoId),
      LendingPartnerId: Number(lendingPartnerId),
      ProcessType: LOAN_PROCESS_TYPE_APPLICATION,
    });
    if (res?.isSuccess === false) {
      throw new Error(parseApiErrorMessage(res, "ثبت فرآیند وام ناموفق بود."));
    }
    return res?.data?.id ?? res?.data?.Id ?? res?.Data?.id ?? res?.Data?.Id ?? null;
  };

  const handleSaveStep = async (form) => {
    if (!editState.step || !editState.domain) return;
    if (editState.domain === "mehrsan" && !canStartMehrsanOperations) {
      toast.error(
        siteVisitOutcomeBlockReason ||
          "تا زمان تأیید محل نیروگاه پس از بازدید اولیه، عملیات مهرسان غیرفعال است."
      );
      return;
    }
    setSaving(true);
    try {
      let processId = null;
      if (editState.domain === "mehrsan") {
        processId = await ensureMehrsanProcess();
        if (!processId) throw new Error("فرآیند مهرسان یافت نشد.");
        const payload = buildStepSavePayload({
          domain: "mehrsan",
          stepId: editState.step.id,
          ...form,
        });
        const res = await MehrsanOperationsApi.addLog(operationsSource, {
          MehrsanProcessId: processId,
          Status: mapUiStatusToMehrsanApiStatus(form.status),
          ...payload,
        });
        if (res?.isSuccess === false) {
          throw new Error(parseApiErrorMessage(res, "ذخیره مرحله مهرسان ناموفق بود."));
        }
        toast.success(
          form.status === "completed"
            ? "مرحله تأیید شد."
            : "مرحله ذخیره شد."
        );
        setEditState({ open: false, domain: null, step: null });
        await loadData();
        onReloadGate?.();
      } else {
        if (loanOperationsReadOnly) {
          toast.info("ثبت و ویرایش مراحل وام در صفحه عملیات لندتک انجام می‌شود.");
          return;
        }
        const requiresCreate = loanProcesses.length === 0 && editState.step.id === 1;
        processId = await ensureLoanProcess(form.lendingPartnerId);
        if (!processId) throw new Error("فرآیند وام یافت نشد.");
        const payload = buildStepSavePayload({
          domain: "loan",
          stepId: editState.step.id,
          ...form,
        });
        const res = await LendingOperationsApi.addLog(operationsSource, {
          LoanProcessId: processId,
          Status: mapUiStatusToLoanApiStatus(form.status),
          TrackingCode: form.referenceCode || null,
          ...payload,
        });
        if (res?.isSuccess === false) {
          throw new Error(parseApiErrorMessage(res, "ذخیره مرحله وام ناموفق بود."));
        }
        const connectorMessage =
          res?.message ||
          res?.Message ||
          res?.data?.connectorMessage ||
          res?.data?.ConnectorMessage ||
          res?.Data?.connectorMessage ||
          res?.Data?.ConnectorMessage;
        if (requiresCreate) {
          toast.success(connectorMessage || "فرآیند وام ثبت شد.");
        } else {
          toast.success(connectorMessage || "مرحله ذخیره شد.");
        }
        setEditState({ open: false, domain: null, step: null });
        await loadData();
        onReloadGate?.();
      }
    } catch (err) {
      toast.error(err?.message || "ذخیره مرحله ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const iriaPortalFields = useMemo(
    () => pickIriaPortalFields(null, activeLoanProcess, null),
    [activeLoanProcess]
  );
  const iriaWorkflowTimeline = useMemo(
    () => resolveLoanWorkflowTimeline(loanProcesses),
    [loanProcesses]
  );
  const iriaExternalStatusCode = useMemo(
    () =>
      pickLoanField(
        activeLoanProcess,
        "partnerExternalStatusCode",
        "PartnerExternalStatusCode"
      ) || "",
    [activeLoanProcess]
  );
  const iriaCurrentStageCode = useMemo(
    () =>
      iriaWorkflowTimeline?.timeline?.currentStageCode ||
      iriaWorkflowTimeline?.timeline?.CurrentStageCode ||
      pickLoanField(activeLoanProcess, "currentStageCode", "CurrentStageCode") ||
      "",
    [iriaWorkflowTimeline, activeLoanProcess]
  );

  const handleTerminateRequest = async () => {
    if (!requestInfoId) return;
    setTerminating(true);
    try {
      const res = await ProjectWorkflowFoundationApi.terminateRequestDueToSiteVisitOutcome(
        operationsSource,
        {
          RequestId: Number(requestInfoId),
          OperatorNote: terminateNote.trim() || null,
        }
      );
      if (!res.ok) {
        throw new Error(res.message || "مختومه‌سازی پرونده ناموفق بود.");
      }
      toast.success(res.message || "پرونده با موفقیت مختومه اعلام شد.");
      setTerminateOpen(false);
      setTerminateNote("");
      onReloadGate?.();
      onRequestTerminated?.();
    } catch (err) {
      toast.error(err?.message || "مختومه‌سازی پرونده ناموفق بود.");
    } finally {
      setTerminating(false);
    }
  };

  const summaryCards = [
    {
      key: "loanRequired",
      label: "وام مورد نیاز",
      value: loanRequired ? "بله" : "خیر",
      icon: loanRequired ? (
        <CheckCircleOutlineIcon color="success" fontSize="small" />
      ) : (
        <InfoOutlinedIcon color="disabled" fontSize="small" />
      ),
    },
    {
      key: "lendingPartner",
      label: "لندتک در حال پیگیری",
      value: loanRequired
        ? activeLendingPartnerLabel || "هنوز انتخاب نشده"
        : "نیاز نیست",
      icon: <CreditCardOutlinedIcon color="secondary" fontSize="small" />,
    },
    {
      key: "mehrsanStatus",
      label: "وضعیت مهرسان",
      value: mehrsanOverall.label,
    },
    {
      key: "loanStatus",
      label: "وضعیت وام",
      value: loanOverall.label,
    },
  ];

  return (
    <Stack spacing={2.5}>
      <Grid container spacing={1.5}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.key}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {card.icon}
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={700}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: canProceedToOperational ? "success.light" : "warning.light",
          bgcolor: canProceedToOperational ? "success.50" : "warning.50",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {canProceedToOperational ? (
            <CheckCircleOutlineIcon color="success" sx={{ mt: 0.25 }} />
          ) : (
            <WarningAmberOutlinedIcon color="warning" sx={{ mt: 0.25 }} />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {GATE_WARNING_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {canProceedToOperational
                ? "شرایط عبور به مرحله بعد برقرار است."
                : GATE_WARNING_TEXT}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip size="small" label={GATE_PILL_MEHRSAN} variant="outlined" />
              {loanRequired ? (
                <Chip size="small" label={GATE_PILL_LOAN} variant="outlined" />
              ) : null}
            </Stack>
            {canProceedToOperational && onNavigateToOperational ? (
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1.5 }}
                onClick={onNavigateToOperational}
              >
                ادامه به برنامه عملیاتی
              </Button>
            ) : loanMehrsanGate?.blockingReason ? (
              <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
                {loanMehrsanGate.blockingReason}
              </Typography>
            ) : null}
            {loanMehrsanGateLoading ? (
              <CircularProgress size={16} sx={{ mt: 1 }} />
            ) : (
              <Button
                size="small"
                variant="text"
                sx={{ mt: 1, px: 0 }}
                onClick={onReloadGate}
              >
                بروزرسانی وضعیت
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      <InitialSiteVisitAssignmentCard
        requestInfoId={requestInfoId}
        workflowSource={operationsSource}
        loanMehrsanGate={loanMehrsanGate}
        canEdit={canEdit}
      />

      {!canStartMehrsanOperations && siteVisitOutcomeBlockReason ? (
        <Alert
          severity={canTerminateDueToSiteVisitRejection ? "error" : "warning"}
          variant="outlined"
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {canTerminateDueToSiteVisitRejection
              ? isApplicantWithdrawal
                ? "انصراف متقاضی ثبت شده است"
                : "محل نیروگاه تأیید نشده است"
              : isAwaitingDistributionReview
                ? "عملیات مهرسان در انتظار تأیید شرکت توزیع"
                : "عملیات مهرسان در انتظار نتیجه بازدید"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {siteVisitOutcomeStatusTitle && !isAwaitingDistributionReview
              ? `نتیجه بازدید: ${siteVisitOutcomeStatusTitle}`
              : siteVisitOutcomeBlockReason}
          </Typography>
          {siteVisitSupervisorDescription ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isApplicantWithdrawal ? "دلیل انصراف ناظر: " : "توضیحات ناظر: "}
              {siteVisitSupervisorDescription}
            </Typography>
          ) : null}
          {canTerminateDueToSiteVisitRejection && canEdit ? (
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={() => setTerminateOpen(true)}
            >
              {isApplicantWithdrawal
                ? "تأیید انصراف و خروج از چرخه"
                : "مختومه اعلام پرونده"}
            </Button>
          ) : null}
        </Alert>
      ) : null}

      <MehrsunSendRegistrationCard
        requestInfoId={requestInfoId}
        operationsSource={operationsSource}
        canEdit={canEdit}
        autoStartToken={mehrsunStartToken}
        highlight={highlightMehrsunSend}
        initialDistributionCompanyId={initialDistributionCompanyId}
        disabledReason={
          canStartMehrsanOperations
            ? ""
            : siteVisitOutcomeBlockReason ||
              "تا زمان تأیید محل نیروگاه پس از بازدید اولیه، عملیات مهرسان غیرفعال است."
        }
        onSuccess={async () => {
          setHighlightMehrsunSend(false);
          await loadData();
          onReloadGate?.();
        }}
      />

      <LoanMehrsanProcessStepsTable
        domain="mehrsan"
        title="عملیات مهرسان"
        steps={mehrsanSteps}
        loading={loading}
        canEditSteps={canEditMehrsanSteps}
        footerNote={
          canStartMehrsanOperations
            ? "مرحله ۱: ارسال اتوماتیک یا «ثبت دستی» کنار آن. سایر مراحل را از جدول تکمیل/به‌روزرسانی کنید."
            : siteVisitOutcomeBlockReason ||
              "تا زمان تأیید محل نیروگاه پس از بازدید اولیه، عملیات مهرسان غیرفعال است."
        }
        onEditStep={(step) => {
          if (!canEditMehrsanSteps) {
            toast.error(
              siteVisitOutcomeBlockReason ||
                "تا زمان تأیید محل نیروگاه پس از بازدید اولیه، عملیات مهرسان غیرفعال است."
            );
            return;
          }
          setEditState({ open: true, domain: "mehrsan", step });
        }}
        onAdvanceStep={(step) => {
          if (!canEditMehrsanSteps) {
            toast.error(
              siteVisitOutcomeBlockReason ||
                "تا زمان تأیید محل نیروگاه پس از بازدید اولیه، عملیات مهرسان غیرفعال است."
            );
            return;
          }
          if (Number(step?.id) === 1) {
            openMehrsunRegistrationFlow();
            return;
          }
          setEditState({ open: true, domain: "mehrsan", step });
        }}
      />

      {loanRequired ? (
        <>
          {loanOperationsReadOnly ? (
            <LendtechSelectionCard
              partners={partners}
              lendingPartnerId={activeLendingPartnerId}
              lendingPartnerCode={activeLendingPartnerCode}
              lendingPartnerLabel={activeLendingPartnerLabel}
              queueSearchHint={lendtechQueueSearchHint}
              canEdit={canEdit}
              sending={lendtechSendBusy}
              changingPartner={lendtechChangeBusy}
              nextBusy={iriaNextBusy}
              canNext={canIriaNext}
              onSend={handleSendToLendtech}
              onNext={handleIriaNextStep}
              onChangePartner={handleChangeLendingPartner}
              autoCreditInquirySubmitted={autoCreditInquirySubmitted}
            />
          ) : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                {loanOperationsReadOnly
                  ? "وضعیت پیگیری لندتک"
                  : "لندتک انتخاب‌شده و در حال پیگیری"}
              </Typography>
              {activeLendingPartnerLabel ? (
                <>
                  <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                    <Chip
                      size="small"
                      color="secondary"
                      label={activeLendingPartnerLabel}
                    />
                    {activeLoanStatusTitle ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`وضعیت: ${activeLoanStatusTitle}`}
                      />
                    ) : null}
                    {activeLoanTrackingCode ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`کد رهگیری: ${activeLoanTrackingCode}`}
                      />
                    ) : null}
                  </Stack>
                  {loanOperationsReadOnly ? (
                    <Typography variant="body2" color="text.secondary">
                      وضعیت مراحل وام از سامانه لندتک نمایش داده می‌شود. برای ثبت
                      اقدامات، از صف عملیات همان لندتک استفاده کنید.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      فرآیند وام این پرونده هم‌اکنون با لندتک{" "}
                      <Box component="span" fontWeight={700}>
                        {activeLendingPartnerLabel}
                      </Box>{" "}
                      پیگیری می‌شود.
                    </Typography>
                  )}
                  {isIriaLoan ? (
                    <Box sx={{ mt: 1.5 }}>
                      <IriaPortalDetailPanel {...iriaPortalFields} compact />
                    </Box>
                  ) : null}
                </>
              ) : loanOperationsReadOnly ? null : (
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  هنوز لندتکی برای این پرونده انتخاب نشده است. با ثبت اولین مرحله وام،
                  لندتک انتخاب و فرآیند با همان لندتک پیگیری می‌شود.
                </Alert>
              )}
            </Stack>
          </Paper>

          {isIriaLoan && activeLendingPartnerLabel ? (
            <IriaPortalStagesTimeline
              loanSteps={loanSteps}
              externalStatusCode={iriaExternalStatusCode}
              currentStageCode={iriaCurrentStageCode}
            />
          ) : null}

          <LoanMehrsanProcessStepsTable
            domain="loan"
            title={
              isIriaLoan
                ? loanOperationsReadOnly
                  ? "وضعیت مراحل وام (ایریاپی)"
                  : "ثبت عملیات مراحل وام (ایریاپی)"
                : activeLendingPartnerLabel
                ? loanOperationsReadOnly
                  ? `وضعیت وام — ${activeLendingPartnerLabel}`
                  : `عملیات وام — ${activeLendingPartnerLabel}`
                : loanOperationsReadOnly
                ? "وضعیت وام"
                : "عملیات وام"
            }
            steps={loanSteps}
            loading={loading}
            readOnly={loanOperationsReadOnly}
            canEditSteps={!loanOperationsReadOnly}
            footerNote={
              loanOperationsReadOnly
                ? "ثبت و پیگیری عملیات مراحل وام در صفحه اختصاصی لندتک انجام می‌شود."
                : "در صورت عدم نیاز به وام، تأیید عدم نیاز وام در بخش مشخصات پروژه ثبت گردد."
            }
            headerExtra={
              activeLendingPartnerLabel ? (
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    لندتک فعال:
                  </Typography>
                  <Chip size="small" color="secondary" variant="outlined" label={activeLendingPartnerLabel} />
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  لندتک هنوز انتخاب نشده است.
                </Typography>
              )
            }
            onEditStep={
              loanOperationsReadOnly
                ? undefined
                : (step) => setEditState({ open: true, domain: "loan", step })
            }
          />
        </>
      ) : (
        <Alert severity="info" variant="outlined">
          این پرونده نیاز به وام ندارد؛ فقط تکمیل فرآیندهای مهرسان کافی است.
        </Alert>
      )}

      <LoanMehrsanStepEditDialog
        open={editState.open}
        onClose={() => setEditState({ open: false, domain: null, step: null })}
        step={editState.step}
        domain={editState.domain}
        fileBaseUrl={fileBaseUrl}
        partners={partners}
        saving={saving}
        quickActionMode={editState.domain === "mehrsan"}
        requiresLoanCreate={
          editState.domain === "loan" &&
          editState.step?.id === 1 &&
          loanProcesses.length === 0
        }
        onSave={handleSaveStep}
      />

      <Dialog
        open={terminateOpen}
        onClose={() => !terminating && setTerminateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isApplicantWithdrawal
            ? "تأیید انصراف و خروج از چرخه"
            : "مختومه اعلام پرونده"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning" variant="outlined">
              {isApplicantWithdrawal
                ? "با تأیید، انصراف متقاضی نهایی می‌شود و درخواست از چرخه خارج می‌گردد."
                : "این عملیات پرونده را مختومه می‌کند و دلیل آن بر اساس نتیجه بازدید ناظر ثبت می‌شود."}
            </Alert>
            {siteVisitOutcomeStatusTitle ? (
              <TextField
                label="نتیجه بازدید"
                value={siteVisitOutcomeStatusTitle}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : null}
            <TextField
              label={isApplicantWithdrawal ? "دلیل انصراف (ناظر)" : "توضیحات ناظر"}
              value={siteVisitSupervisorDescription}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="توضیح تکمیلی کارشناس (اختیاری)"
              value={terminateNote}
              onChange={(e) => setTerminateNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateOpen(false)} disabled={terminating}>
            بستن
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleTerminateRequest}
            disabled={terminating}
          >
            {terminating
              ? "در حال ثبت..."
              : isApplicantWithdrawal
                ? "تأیید انصراف"
                : "تأیید مختومه‌سازی"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

LoanMehrsanStagePanel.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  workflowSource: PropTypes.string,
  loanMehrsanGate: PropTypes.object,
  loanMehrsanGateLoading: PropTypes.bool,
  canProceedToOperational: PropTypes.bool,
  canEdit: PropTypes.bool,
  loanOperationsReadOnly: PropTypes.bool,
  onReloadGate: PropTypes.func,
  onNavigateToOperational: PropTypes.func,
  onRequestTerminated: PropTypes.func,
  initialDistributionCompanyId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default LoanMehrsanStagePanel;
